/**
 * Anwendungsrahmen: Anmeldeschranke, Navigation und Routen.
 *
 * Die Routen sind zusaetzlich mit Berechtigungspruefungen versehen. Diese
 * dienen nur der Benutzerfuehrung – die verbindliche Pruefung erfolgt in der API.
 */
import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { makeStyles, tokens } from '@fluentui/react-components';
import { SessionProvider, useSession } from './lib/session.js';
import { AppShell } from './components/AppShell.js';
import { CatalogShell } from './components/CatalogShell.js';
import { isCatalogHost } from './lib/catalogHost.js';
import { SignInPage } from './pages/SignInPage.js';
import { AccessDeniedView, ErrorView, LoadingView } from './components/StateViews.js';
import { PERMISSIONS, type Permission } from './api/types.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { useI18n } from './i18n/index.js';

// Selten genutzte Bereiche werden erst bei Bedarf geladen.
const CountryReportPage = lazy(() => import('./pages/CountryReportPage.js').then((m) => ({ default: m.CountryReportPage })));
const AssignmentsPage = lazy(() => import('./pages/AssignmentsPage.js').then((m) => ({ default: m.AssignmentsPage })));
const PeoplePage = lazy(() => import('./pages/PeoplePage.js').then((m) => ({ default: m.PeoplePage })));
const OrphanLicensesPage = lazy(() =>
  import('./pages/OrphanLicensesPage.js').then((m) => ({ default: m.OrphanLicensesPage })),
);
const SyncPage = lazy(() => import('./pages/SyncPage.js').then((m) => ({ default: m.SyncPage })));
const AdminSourcesPage = lazy(() => import('./pages/AdminSourcesPage.js').then((m) => ({ default: m.AdminSourcesPage })));
const AdminPricesPage = lazy(() => import('./pages/AdminPricesPage.js').then((m) => ({ default: m.AdminPricesPage })));
const AdminConnectorsPage = lazy(() =>
  import('./pages/AdminConnectorsPage.js').then((m) => ({ default: m.AdminConnectorsPage })),
);
const CatalogPage = lazy(() => import('./pages/CatalogPage.js').then((m) => ({ default: m.CatalogPage })));
const MyDevicesPage = lazy(() => import('./pages/MyDevicesPage.js').then((m) => ({ default: m.MyDevicesPage })));
const MySoftwarePage = lazy(() => import('./pages/MySoftwarePage.js').then((m) => ({ default: m.MySoftwarePage })));
const CatalogCartPage = lazy(() => import('./pages/CatalogCartPage.js').then((m) => ({ default: m.CatalogCartPage })));
const MyRequestsPage = lazy(() => import('./pages/MyRequestsPage.js').then((m) => ({ default: m.MyRequestsPage })));
const ApprovalsPage = lazy(() => import('./pages/ApprovalsPage.js').then((m) => ({ default: m.ApprovalsPage })));
const NewSoftwareRequestPage = lazy(() => import('./pages/NewSoftwareRequestPage.js').then((m) => ({ default: m.NewSoftwareRequestPage })));
const NewUserRequestPage = lazy(() => import('./pages/NewUserRequestPage.js').then((m) => ({ default: m.NewUserRequestPage })));
const CatalogRequestsAdminPage = lazy(() =>
  import('./pages/CatalogRequestsAdminPage.js').then((m) => ({ default: m.CatalogRequestsAdminPage })),
);
const AssetsAdminPage = lazy(() => import('./pages/AssetsAdminPage.js').then((m) => ({ default: m.AssetsAdminPage })));
const CatalogAdminPage = lazy(() => import('./pages/CatalogAdminPage.js').then((m) => ({ default: m.CatalogAdminPage })));
const CatalogCategoriesPage = lazy(() =>
  import('./pages/CatalogCategoriesPage.js').then((m) => ({ default: m.CatalogCategoriesPage })),
);
const AuditLogPage = lazy(() => import('./pages/AuditLogPage.js').then((m) => ({ default: m.AuditLogPage })));

const useStyles = makeStyles({
  notFound: { padding: tokens.spacingVerticalXXXL, textAlign: 'center' },
});

/** Eine Berechtigung oder eine Liste, von der eine genuegt. */
function RequirePermission({ permission, children }: { permission: Permission | Permission[]; children: JSX.Element }) {
  const { has } = useSession();
  const erlaubt = Array.isArray(permission) ? permission.some((p) => has(p)) : has(permission);
  return erlaubt ? children : <AccessDeniedView />;
}

function NotFound() {
  const styles = useStyles();
  const { t } = useI18n();
  return <div className={styles.notFound}>{t('common.notFound')}</div>;
}

function AuthenticatedApp() {
  const { profile, loading, error, reload } = useSession();
  const { t } = useI18n();
  const location = useLocation();
  const catalogOnly = isCatalogHost(window.location.hostname);

  if (loading) return <LoadingView />;
  if (error) return <ErrorView error={error} onRetry={reload} />;

  // Authentifiziert, aber ohne zugewiesene App-Rolle.
  if (!profile || profile.roles.length === 0) {
    const Shell = catalogOnly ? CatalogShell : AppShell;
    return (
      <Shell>
        <AccessDeniedView />
        <p style={{ textAlign: 'center' }}>{t('auth.noRoleHint')}</p>
      </Shell>
    );
  }

  // Der Katalog bekommt eine eigene, schlanke Huelle: Wer nur wissen will,
  // welches Notebook er bestellen darf, soll nicht in einem
  // Controllingwerkzeug landen. Dieselbe Anwendung, dieselbe Anmeldung,
  // dieselben Berechtigungen – getrennt ist nur die Darstellung.
  // Beide Seiten richten sich an alle Mitarbeitenden und teilen sich deshalb
  // dieselbe schlanke Huelle.
  // Die Startseite ist der Katalog: Er richtet sich an alle; die
  // Lizenzverwaltung erreicht, wer sie braucht, ueber das Kontomenue oder
  // direkt unter /dashboard.
  const catalogPaths = [
    '/',
    '/katalog',
    '/katalog/warenkorb',
    '/katalog/meine-anfragen',
    '/katalog/neue-software',
    '/katalog/userantrag',
    '/katalog/freigaben',
    '/meine-geraete',
    '/meine-software',
  ];
  if (catalogOnly || catalogPaths.includes(location.pathname)) {
    return (
      <CatalogShell>
        <Suspense fallback={<LoadingView />}>
          <Routes>
            <Route
              path="/katalog"
              element={
                <RequirePermission permission={PERMISSIONS.CatalogRead}>
                  <CatalogPage />
                </RequirePermission>
              }
            />
            <Route
              path="/meine-geraete"
              element={
                <RequirePermission permission={PERMISSIONS.AssetRead}>
                  <MyDevicesPage />
                </RequirePermission>
              }
            />
            <Route
              path="/meine-software"
              element={
                <RequirePermission permission={PERMISSIONS.LicenseReadOwn}>
                  <MySoftwarePage />
                </RequirePermission>
              }
            />
            <Route
              path="/katalog/warenkorb"
              element={
                <RequirePermission permission={PERMISSIONS.CatalogRequest}>
                  <CatalogCartPage />
                </RequirePermission>
              }
            />
            <Route
              path="/katalog/meine-anfragen"
              element={
                <RequirePermission permission={[PERMISSIONS.CatalogRequest, PERMISSIONS.UserRequestCreate]}>
                  <MyRequestsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/katalog/freigaben"
              element={
                <RequirePermission permission={PERMISSIONS.CatalogRequest}>
                  <ApprovalsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/katalog/neue-software"
              element={
                <RequirePermission permission={PERMISSIONS.CatalogRequest}>
                  <NewSoftwareRequestPage />
                </RequirePermission>
              }
            />
            <Route
              path="/katalog/userantrag"
              element={
                <RequirePermission permission={PERMISSIONS.UserRequestCreate}>
                  <NewUserRequestPage />
                </RequirePermission>
              }
            />
            {/* Unter einem Katalog-Hostnamen fuehren alle uebrigen Pfade in den
                Katalog statt ins Leere. */}
            <Route path="*" element={<Navigate to="/katalog" replace />} />
          </Routes>
        </Suspense>
      </CatalogShell>
    );
  }

  return (
    <AppShell>
      <Suspense fallback={<LoadingView />}>
        <Routes>
          <Route path="/" element={<Navigate to="/katalog" replace />} />
          <Route
            path="/dashboard"
            element={
              <RequirePermission permission={PERMISSIONS.DashboardRead}>
                <DashboardPage />
              </RequirePermission>
            }
          />
          <Route
            path="/laender"
            element={
              <RequirePermission permission={PERMISSIONS.ReportRead}>
                <CountryReportPage />
              </RequirePermission>
            }
          />
          <Route
            path="/zuweisungen"
            element={
              <RequirePermission permission={PERMISSIONS.ReportRead}>
                <AssignmentsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/personen"
            element={
              <RequirePermission permission={PERMISSIONS.ReportRead}>
                <PeoplePage />
              </RequirePermission>
            }
          />
          <Route
            path="/verwaiste-lizenzen"
            element={
              <RequirePermission permission={PERMISSIONS.ReportRead}>
                <OrphanLicensesPage />
              </RequirePermission>
            }
          />
          <Route
            path="/synchronisierung"
            element={
              <RequirePermission permission={PERMISSIONS.SyncRead}>
                <SyncPage />
              </RequirePermission>
            }
          />
          {/* Die Katalogübersicht liegt bewusst NICHT hier, sondern in einer
              eigenen Huelle ohne die Navigation der Lizenzverwaltung – siehe
              AuthenticatedApp. Die Pflegeseiten bleiben hier, weil sie zur
              Verwaltungsarbeit gehoeren. Die Pruefung ist Benutzerfuehrung;
              verbindlich entscheidet der Server. */}
          <Route
            path="/katalog/verwaltung"
            element={
              <RequirePermission permission={PERMISSIONS.CatalogWrite}>
                <CatalogAdminPage />
              </RequirePermission>
            }
          />
          <Route
            path="/katalog/kategorien"
            element={
              <RequirePermission permission={PERMISSIONS.CatalogWrite}>
                <CatalogCategoriesPage />
              </RequirePermission>
            }
          />

          {/* Der Bestand gehoert zur Verwaltungsarbeit. Die Selbstauskunft
              „Meine Geräte“ liegt dagegen in der Katalog-Huelle – siehe oben. */}
          <Route
            path="/geraete"
            element={
              <RequirePermission permission={PERMISSIONS.AssetManage}>
                <AssetsAdminPage />
              </RequirePermission>
            }
          />

          {/* Administration ist eine Gruppe von Unterseiten; der Sammelpfad
              fuehrt auf die erste. Der alte Pfad /audit bleibt als Weiterleitung
              gueltig, damit gespeicherte Verweise weiter funktionieren. */}
          <Route path="/administration" element={<Navigate to="/administration/quellen" replace />} />
          <Route
            path="/administration/quellen"
            element={
              <RequirePermission permission={PERMISSIONS.ProviderRead}>
                <AdminSourcesPage />
              </RequirePermission>
            }
          />
          <Route
            path="/administration/preise"
            element={
              <RequirePermission permission={PERMISSIONS.ProviderRead}>
                <AdminPricesPage />
              </RequirePermission>
            }
          />
          <Route
            path="/administration/anbindungen"
            element={
              <RequirePermission permission={PERMISSIONS.ProviderRead}>
                <AdminConnectorsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/administration/anfragen"
            element={
              <RequirePermission permission={PERMISSIONS.CatalogRequestManage}>
                <CatalogRequestsAdminPage />
              </RequirePermission>
            }
          />
          <Route
            path="/administration/audit"
            element={
              <RequirePermission permission={PERMISSIONS.AuditRead}>
                <AuditLogPage />
              </RequirePermission>
            }
          />
          <Route path="/audit" element={<Navigate to="/administration/audit" replace />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </AppShell>
  );
}

export function App() {
  return (
    <>
      <AuthenticatedTemplate>
        <SessionProvider>
          <AuthenticatedApp />
        </SessionProvider>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SignInPage />
      </UnauthenticatedTemplate>
    </>
  );
}
